import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Briefcase, Eye, Users, Plus, Edit, Trash2, 
  MoreVertical, CheckCircle, XCircle, MessageCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { createDirectContact, ensureUserExists } from '../services/supabaseFriendChat';

const API = process.env.REACT_APP_BACKEND_URL;

const EmployerDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [metrics, setMetrics] = useState({ active_jobs: 0, total_views: 0, total_applications: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [showApplicants, setShowApplicants] = useState(false);
  const [confirmDeleteJobId, setConfirmDeleteJobId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/employer`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs);
        setMetrics(data.metrics);
      }
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicants = async (jobId, jobTitle) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${jobId}/applicants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setApplicants(data.applications);
        setSelectedJob({ id: jobId, title: jobTitle });
        setShowApplicants(true);
      }
    } catch (error) {
      toast.error('Failed to fetch applicants');
    }
  };

  const updateApplicantStatus = async (jobId, applicationId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${jobId}/applicants/${applicationId}/status?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Applicant ${status}`);
        fetchApplicants(jobId, selectedJob.title);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteJob = async (jobId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Job deleted');
        fetchJobs();
      } else {
        toast.error(data.detail || 'Failed to delete job');
      }
    } catch (error) {
      toast.error('Failed to delete job');
    }
    setDeleting(false);
    setConfirmDeleteJobId(null);
  };

  const toggleJobStatus = async (job) => {
    const newStatus = job.status === 'active' ? 'closed' : 'active';
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${job.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Job ${newStatus === 'active' ? 'activated' : 'closed'}`);
        fetchJobs();
      }
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const handleContactApplicant = async (applicant) => {
    try {
      // Ensure current user exists in Supabase
      await ensureUserExists(user.id, user.full_name, user.avatar_url || null);
      
      // Create auto message
      const autoMessage = `You have been shortlisted for the '${selectedJob.title}' position.`;
      
      // Create direct contact and send message with 'job' contact type
      const result = await createDirectContact(
        user.id,
        user.full_name,
        applicant.applicant_id,
        applicant.applicant_name,
        autoMessage,
        'job' // Contact type for job-related contact
      );
      
      if (result.success) {
        // Update applicant status to contacted
        await updateApplicantStatus(selectedJob.id, applicant.id, 'contacted');
        
        // Dispatch event to open FloatingChatWidget with this conversation
        window.dispatchEvent(new CustomEvent('openDirectChat', {
          detail: {
            conversationId: result.conversationId,
            friendId: applicant.applicant_id,
            friendName: applicant.applicant_name
          }
        }));
        
        toast.success('Chat opened with applicant');
      } else {
        toast.error(result.error || 'Failed to contact applicant');
      }
    } catch (error) {
      console.error('Error contacting applicant:', error);
      toast.error('Failed to contact applicant');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'draft': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'closed': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getApplicantStatusColor = (status) => {
    switch (status) {
      case 'applied': return 'bg-blue-100 text-blue-700';
      case 'shortlisted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'contacted': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (showApplicants && selectedJob) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Button variant="ghost" onClick={() => setShowApplicants(false)} className="mb-2">
                ← Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Applicants for {selectedJob.title}</h1>
              <p className="text-gray-600">{applicants.length} applicant(s)</p>
            </div>
          </div>

          {applicants.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No applicants yet</h3>
                <p className="text-gray-500">Check back later for applications</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {applicants.map((applicant) => (
                <Card key={applicant.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-lg font-semibold text-orange-600">
                              {applicant.applicant_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{applicant.applicant_name}</h3>
                            <p className="text-sm text-gray-500">{applicant.applicant_email}</p>
                          </div>
                          <Badge className={getApplicantStatusColor(applicant.status)}>
                            {applicant.status}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                          {applicant.applicant_mobile && (
                            <div>
                              <span className="text-gray-500">Mobile:</span>
                              <span className="ml-1 font-medium">{applicant.applicant_mobile}</span>
                            </div>
                          )}
                          {applicant.applicant_college && (
                            <div>
                              <span className="text-gray-500">College:</span>
                              <span className="ml-1 font-medium">{applicant.applicant_college}</span>
                            </div>
                          )}
                          {applicant.applicant_degree && (
                            <div>
                              <span className="text-gray-500">Degree:</span>
                              <span className="ml-1 font-medium">{applicant.applicant_degree}</span>
                            </div>
                          )}
                          {applicant.applicant_status && (
                            <div>
                              <span className="text-gray-500">Status:</span>
                              <span className="ml-1 font-medium">{applicant.applicant_status}</span>
                            </div>
                          )}
                        </div>

                        {applicant.cover_note && (
                          <div className="bg-gray-50 p-3 rounded-lg mb-3">
                            <p className="text-sm text-gray-500 mb-1">Why they're the best candidate:</p>
                            <p className="text-gray-700">{applicant.cover_note}</p>
                          </div>
                        )}

                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>Applied: {new Date(applicant.applied_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {applicant.cv_url && (
                          <a
                            href={applicant.cv_url.startsWith('data:') ? applicant.cv_url : `${API}${applicant.cv_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200 text-sm font-medium"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Download CV
                          </a>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => updateApplicantStatus(selectedJob.id, applicant.id, 'shortlisted')}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Shortlist
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => updateApplicantStatus(selectedJob.id, applicant.id, 'rejected')}
                          >
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                            onClick={() => handleContactApplicant(applicant)}
                          >
                            <MessageCircle className="h-4 w-4 mr-1" /> Contact
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Briefcase className="h-8 w-8 text-orange-500" />
              Employer Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Manage your job postings and applicants</p>
          </div>
          <Button 
            onClick={() => navigate('/post-job')}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Post a New Job
          </Button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Briefcase className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Jobs</p>
                  <p className="text-2xl font-bold text-gray-900">{metrics.active_jobs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Eye className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Views</p>
                  <p className="text-2xl font-bold text-gray-900">{metrics.total_views}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Applications</p>
                  <p className="text-2xl font-bold text-gray-900">{metrics.total_applications}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Jobs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Your Job Postings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No jobs posted yet</h3>
                <p className="text-gray-500 mb-4">Start by posting your first job</p>
                <Button onClick={() => navigate('/post-job')} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="h-4 w-4 mr-2" /> Post a New Job
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-gray-500">Job Title</th>
                      <th className="pb-3 font-medium text-gray-500">Status</th>
                      <th className="pb-3 font-medium text-gray-500">Views</th>
                      <th className="pb-3 font-medium text-gray-500">Applications</th>
                      <th className="pb-3 font-medium text-gray-500">Posted</th>
                      <th className="pb-3 font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.id} className="border-b hover:bg-gray-50">
                        <td className="py-4">
                          <div>
                            <p className="font-medium text-gray-900">{job.title}</p>
                            <p className="text-sm text-gray-500">{job.company_name} • {job.location}</p>
                          </div>
                        </td>
                        <td className="py-4">
                          <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                        </td>
                        <td className="py-4 text-gray-600">{job.views}</td>
                        <td className="py-4">
                          <button 
                            onClick={() => fetchApplicants(job.id, job.title)}
                            className="text-orange-600 hover:text-orange-700 font-medium"
                          >
                            {job.applications_count} applicants
                          </button>
                        </td>
                        <td className="py-4 text-gray-500 text-sm">
                          {new Date(job.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => fetchApplicants(job.id, job.title)}>
                                <Users className="h-4 w-4 mr-2" /> View Applicants
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/post-job?edit=${job.id}`)}>
                                <Edit className="h-4 w-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toggleJobStatus(job)}>
                                {job.status === 'active' ? (
                                  <><XCircle className="h-4 w-4 mr-2" /> Close Job</>
                                ) : (
                                  <><CheckCircle className="h-4 w-4 mr-2" /> Activate</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setConfirmDeleteJobId(job.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inline delete confirmation — replaces window.confirm() which is blocked in WebViews */}
      {confirmDeleteJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-gray-900 text-lg mb-1">Delete Job Posting?</h3>
            <p className="text-gray-500 text-sm mb-5">This action cannot be undone. All applications for this job will also be removed.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeleteJobId(null)}
                className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteJob(confirmDeleteJobId)}
                disabled={deleting}
                className="flex-1 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-60"
                data-testid="confirm-delete-job-btn"
              >
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerDashboard;
